import type { ReactNode } from 'react';

export type StatusTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'accent'
  | 'primary';

const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  neutral: 'bg-gray-50 text-gray-600 border-gray-200',
  accent: 'bg-orange-50 text-orange-700 border-orange-200',
  primary: 'bg-primary/10 text-primary border-primary/20',
};

/**
 * Archer-style bordered pill badge. Use for status fields in tables, filter
 * chips, and inline counts.
 */
export function StatusBadge({
  tone = 'neutral',
  children,
  count,
}: {
  tone?: StatusTone;
  children: ReactNode;
  count?: number;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium border ${TONE_CLASSES[tone]}`}
    >
      {children}
      {count !== undefined && <span className="font-semibold">{count}</span>}
    </span>
  );
}

/**
 * A row of status chips with a leading label, like Archer's filter strip
 * (TOTAL 35,396 . STAGE [Completed 33512] [Map Charge Codes 1208] …).
 */
export function StatusStrip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {label}
      </span>
      {children}
    </div>
  );
}
